/**
 * timeline — unified event log for human edits + agent fires.
 *
 * Every project mutation (designer adds a SKU from catalog, agent
 * rejects a placement, NKBA expert fires a warning, milestone gets
 * synthesized, approval signed, render queued) writes ONE typed
 * :TimelineEvent. Same shape regardless of actor.
 *
 * The Timeline panel under /design renders these chronologically.
 * Because both actors write the SAME node kind, the feed is
 * structurally in-sync — never "agent action eventually shows up";
 * it shows up the moment it happens, alongside the human edit
 * that triggered it.
 */
import type { GraphNode } from "../../types/ontology";
import codegen from "../../../../docs/ontology/codegen.json";

/** Verbs the timeline recognises. Same vocabulary across human + agent
 *  events; the cockpit colors them differently by `verdict` only. */
export type TimelineVerb =
  | "added"
  | "removed"
  | "proposed"
  | "rejected"
  | "accepted"
  | "synthesized"
  | "approved"
  | "promoted"
  | "drafted"
  | "viewed"
  | "exported";

export interface TimelineEventInput {
  readonly actorKind: "human" | "agent";
  /** For human: user email or "designer". For agent: :Agent.id from codegen. */
  readonly actorId: string;
  readonly verb: TimelineVerb;
  readonly subjectKind: string;
  readonly subjectId: string;
  readonly subjectLabel: string;
  readonly verdict: "info" | "warn" | "block";
  /** One-line human-readable summary. */
  readonly summary: string;
  /** Optional citation (NBC-2016, NKBA-G5, user/catalog-rail, ...). */
  readonly citation?: string;
  /** Optional fix / detail string. */
  readonly fix?: string;
}

export interface TimelineEvent extends TimelineEventInput {
  readonly id: string;
  readonly atTime: string; // ISO 8601, UTC
}

const KEY_PREFIX = "foyrmind.timeline.";
/** Hard cap on persisted events per project — keep localStorage bounded.
 *  Newest-first; rotate oldest out when we hit the cap. */
const MAX_EVENTS_PER_PROJECT = 500;

export function recordEvent(projectId: string, input: TimelineEventInput): TimelineEvent {
  const atTime = new Date().toISOString();
  const ev: TimelineEvent = {
    ...input,
    id: `tl-${projectId}-${atTime.replace(/[:.]/g, "-")}-${Math.random().toString(36).slice(2, 8)}`,
    atTime,
  };
  try {
    const existing = readTimeline(projectId);
    const next = [ev, ...existing].slice(0, MAX_EVENTS_PER_PROJECT);
    window.localStorage.setItem(KEY_PREFIX + projectId, JSON.stringify(next));
    // Notify listeners (Timeline panel) so the feed updates immediately
    // without waiting for a tick.
    window.dispatchEvent(new CustomEvent("foyrmind:timeline", { detail: { projectId, event: ev } }));
  } catch (err) {
    // CLAUDE.md §5 — surface, never silently swallow.
    console.error("[timeline] failed to persist event", err);
  }
  return ev;
}

export function readTimeline(projectId: string): ReadonlyArray<TimelineEvent> {
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + projectId);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((e): e is TimelineEvent =>
      Boolean(e) && typeof e === "object"
      && typeof (e as TimelineEvent).id === "string"
      && typeof (e as TimelineEvent).atTime === "string",
    );
  } catch (err) {
    console.error("[timeline] failed to read", err);
    return [];
  }
}

export function clearTimeline(projectId: string): void {
  try { window.localStorage.removeItem(KEY_PREFIX + projectId); }
  catch (err) { console.error("[timeline] failed to clear", err); }
}

// ─── Persona resolution ─────────────────────────────────────────────────

export interface ResolvedActor {
  readonly kind: "human" | "agent";
  readonly id: string;
  readonly displayName: string;
  readonly title: string;
  readonly initials: string;
  readonly color: string;
}

interface AgentRow {
  id: string;
  personaName?: string;
  personaTitle?: string;
  personaInitials?: string;
  personaColor?: string;
  expert?: string;
}

const AGENT_PERSONAS = new Map<string, AgentRow>(
  ((codegen.agents ?? []) as AgentRow[]).map((a) => [a.id, a]),
);

/** Resolve a timeline event's actor to a persona-rich display object.
 *  Human actors fall back to the email's local part for initials. */
export function resolveActor(actorKind: "human" | "agent", actorId: string): ResolvedActor {
  if (actorKind === "agent") {
    const a = AGENT_PERSONAS.get(actorId);
    if (a) {
      return {
        kind: "agent",
        id: actorId,
        displayName: a.personaName ?? actorId,
        title: a.personaTitle ?? a.expert ?? "Specialist agent",
        initials: a.personaInitials ?? actorId.slice(0, 2).toUpperCase(),
        color: a.personaColor ?? "#7A3E5F",
      };
    }
    return { kind: "agent", id: actorId, displayName: actorId, title: "Agent", initials: actorId.slice(0, 2).toUpperCase(), color: "#7A3E5F" };
  }
  // Human actor — derive readable name + initials from email-like id.
  const local = actorId.split("@")[0] ?? actorId;
  const tokens = local.split(/[.\-_]/).filter(Boolean);
  const display = tokens.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" ") || "Designer";
  const initials = tokens.length > 1
    ? (tokens[0]!.charAt(0) + tokens[tokens.length - 1]!.charAt(0)).toUpperCase()
    : local.slice(0, 2).toUpperCase();
  // Avoid "Designer · Designer" when the email isn't set and we fall
  // back to the literal placeholder id "designer" — collapse the title
  // when it would just echo the display name.
  const title = display === "Designer" ? "" : "Designer";
  return {
    kind: "human",
    id: actorId,
    displayName: display,
    title,
    initials,
    color: "#3D6B9D",
  };
}

// ─── Graph projection ───────────────────────────────────────────────────

/** Project the persisted timeline events into typed :TimelineEvent
 *  GraphNodes so the Why-engine + Project map can read them like any
 *  other node. */
export function asGraphNodes(projectId: string): ReadonlyArray<GraphNode> {
  return readTimeline(projectId).map((e) => ({
    id: e.id,
    kind: "TimelineEvent",
    label: `${e.actorKind === "human" ? "designer" : e.actorId} ${e.verb} ${e.subjectLabel}`,
    attrs: {
      atTime: e.atTime,
      actorKind: e.actorKind,
      actorId: e.actorId,
      verb: e.verb,
      subjectKind: e.subjectKind,
      subjectId: e.subjectId,
      verdict: e.verdict,
      summary: e.summary,
      ...(e.citation !== undefined ? { citation: e.citation } : {}),
      ...(e.fix !== undefined ? { fix: e.fix } : {}),
    },
  }));
}
